import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Truck, CheckCircle2, Lock, AlertTriangle, MapPin, Phone, Route, StickyNote } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import AppFooter from "../components/AppFooter";
import { api, formatDate } from "../lib/apiClient";

const HEADER_IMG =
  "https://images.pexels.com/photos/11087837/pexels-photo-11087837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function PublicQuoteForm() {
  const { token } = useParams();
  const [inquiry, setInquiry] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone_number: "",
    rate: "",
    vehicle_type: "",
    availability: "",
    remarks: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/inquiry/${token}`);
        setInquiry(res.data);
      } catch (err) {
        setError(
          err?.response?.status === 404
            ? "This link is invalid or has been removed."
            : "Something went wrong. Please try again."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.phone_number ||
      !form.rate ||
      !form.vehicle_type ||
      !form.availability
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    setConfirmOpen(true);
  };

  const confirmSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/public/inquiry/${token}/quote`, {
        ...form,
        rate: parseFloat(form.rate),
      });
      setConfirmOpen(false);
      setSubmitted(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to submit quote"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white text-zinc-500">
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-white px-4">
        <div className="max-w-md w-full border-2 border-zinc-900 p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-[#DC2626] mx-auto mb-3" />
          <h1 className="text-2xl font-bold tracking-tight mb-1">
            Link Unavailable
          </h1>
          <p className="text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }

  const isLocked =
    inquiry.status === "closed" || inquiry.status === "expired";

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <div
          className="relative h-40 md:h-48 bg-zinc-900"
          style={{
            backgroundImage: `url(${HEADER_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="max-w-xl mx-auto px-4 -mt-16 relative z-10 pb-16">
          <div
            data-testid="quote-submitted"
            className="bg-white border-2 border-zinc-900 p-8 md:p-10 text-center"
          >
            <CheckCircle2 className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
            <div className="label-overline mb-2">Submitted</div>
            <h1 className="text-3xl font-bold tracking-tighter mb-2">
              Quote Received
            </h1>
            <p className="text-zinc-600 mb-6">
              Thank you! Your quote has been shared with the inquiry owner.
              They will get back to you if selected.
            </p>
            <div className="border-t border-zinc-200 pt-6 text-left space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="label-overline">Route</span>
                <span className="font-semibold">
                  {inquiry.from_city} → {inquiry.to_city}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="label-overline">Material</span>
                <span className="font-semibold">{inquiry.material}</span>
              </div>
              {inquiry.distance_km !== null && inquiry.distance_km !== undefined && (
                <div className="flex justify-between">
                  <span className="label-overline">KM</span>
                  <span className="data-cell font-semibold">
                    {inquiry.distance_km}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="label-overline">Weight</span>
                <span className="data-cell font-semibold">
                  {inquiry.weight} {inquiry.weight_unit}
                </span>
              </div>
            </div>
          </div>
        </div>
        <AppFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Header */}
      <div
        className="relative h-44 md:h-56 bg-zinc-900"
        style={{
          backgroundImage: `url(${HEADER_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-xl mx-auto px-4 h-full flex items-end pb-5">
          <div className="text-white">
            <div className="flex items-center gap-2 mb-1">
              <Truck className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">
                Transport Inquiry
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tighter">
              Submit Your Quote
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 md:py-8 pb-16">
        {/* Receipt-style inquiry detail */}
        <div
          data-testid="inquiry-receipt"
          className="border border-zinc-900 bg-white mb-6"
        >
          <div className="p-4 md:p-5 border-b border-zinc-200 flex items-center justify-between gap-2">
            <div className="label-overline">Load Details</div>
            <StatusBadge status={inquiry.status} />
          </div>
          <dl className="p-4 md:p-5 space-y-3 text-sm">
            <div className="flex items-start justify-between gap-4">
              <dt className="label-overline pt-0.5">From</dt>
              <dd className="font-semibold text-right text-zinc-900 text-base">
                {inquiry.from_city}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-4">
              <dt className="label-overline pt-0.5">To</dt>
              <dd className="font-semibold text-right text-zinc-900 text-base">
                {inquiry.to_city}
              </dd>
            </div>
            {inquiry.pickup_address && (
              <div className="flex items-start justify-between gap-4">
                <dt className="label-overline pt-0.5 inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Pickup
                </dt>
                <dd className="text-right text-zinc-800 max-w-[68%]">
                  {inquiry.pickup_address}
                </dd>
              </div>
            )}
            {inquiry.drop_address && (
              <div className="flex items-start justify-between gap-4">
                <dt className="label-overline pt-0.5 inline-flex items-center gap-1">
                  <Route className="h-3 w-3" />
                  Drop
                </dt>
                <dd className="text-right text-zinc-800 max-w-[68%]">
                  {inquiry.drop_address}
                </dd>
              </div>
            )}
            {inquiry.distance_km !== null && inquiry.distance_km !== undefined && (
              <div className="flex items-start justify-between gap-4">
                <dt className="label-overline pt-0.5">KM</dt>
                <dd className="data-cell font-semibold text-right text-zinc-900 text-base">
                  {inquiry.distance_km}
                </dd>
              </div>
            )}
            <div className="flex items-start justify-between gap-4">
              <dt className="label-overline pt-0.5">Weight</dt>
              <dd className="data-cell font-semibold text-right text-zinc-900 text-base">
                {inquiry.weight} {inquiry.weight_unit}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-4">
              <dt className="label-overline pt-0.5">Material</dt>
              <dd className="font-semibold text-right text-zinc-900 text-base">
                {inquiry.material}
              </dd>
            </div>
            {inquiry.load_notes && (
              <div className="flex items-start justify-between gap-4">
                <dt className="label-overline pt-0.5 inline-flex items-center gap-1">
                  <StickyNote className="h-3 w-3" />
                  Notes
                </dt>
                <dd className="text-right text-zinc-800 max-w-[68%]">
                  {inquiry.load_notes}
                </dd>
              </div>
            )}
            <div className="flex items-start justify-between gap-4 pt-2 border-t border-dashed border-zinc-200">
              <dt className="label-overline pt-0.5">Expires</dt>
              <dd className="data-cell text-zinc-600 text-right">
                {formatDate(inquiry.expires_at)}
              </dd>
            </div>
          </dl>
        </div>

        {isLocked ? (
          <div
            data-testid="locked-notice"
            className="border-2 border-[#DC2626] p-6 text-center"
          >
            <Lock className="h-8 w-8 text-[#DC2626] mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-1">Quotes Not Accepted</h2>
            <p className="text-zinc-600 text-sm">
              {inquiry.status === "closed"
                ? "The owner has closed this inquiry."
                : "This link has expired and is no longer accepting quotes."}
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="space-y-5"
          >
            <div className="label-overline mb-1">Your Quote</div>

            <div className="space-y-1.5">
              <Label htmlFor="name" className="label-overline">
                Your Name / Company
              </Label>
              <Input
                id="name"
                data-testid="input-name"
                placeholder="e.g. Rajesh Transport Co."
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 text-base"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone" className="label-overline">
                Phone Number
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
                <Input
                  id="phone"
                  data-testid="input-phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="e.g. +91 98765 43210"
                  value={form.phone_number}
                  onChange={(e) =>
                    setForm({ ...form, phone_number: e.target.value })
                  }
                  className="h-12 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 text-base pl-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rate" className="label-overline">
                Rate (₹ INR)
              </Label>
              <Input
                id="rate"
                data-testid="input-rate"
                type="number"
                inputMode="decimal"
                step="1"
                min="0"
                placeholder="e.g. 45000"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                className="h-12 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 text-base data-cell"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="vehicle" className="label-overline">
                Vehicle Type
              </Label>
              <Input
                id="vehicle"
                data-testid="input-vehicle"
                placeholder="e.g. 32ft Multi-axle, Container 20ft"
                value={form.vehicle_type}
                onChange={(e) =>
                  setForm({ ...form, vehicle_type: e.target.value })
                }
                className="h-12 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 text-base"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="availability" className="label-overline">
                Availability
              </Label>
              <Input
                id="availability"
                data-testid="input-availability"
                placeholder="e.g. Tomorrow morning, Within 24 hours"
                value={form.availability}
                onChange={(e) =>
                  setForm({ ...form, availability: e.target.value })
                }
                className="h-12 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 text-base"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="remarks" className="label-overline">
                Remarks (optional)
              </Label>
              <Textarea
                id="remarks"
                data-testid="input-remarks"
                placeholder="Any conditions, detention charges, loading/unloading notes…"
                value={form.remarks}
                onChange={(e) =>
                  setForm({ ...form, remarks: e.target.value })
                }
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 text-base min-h-[90px]"
              />
            </div>

            <Button
              type="submit"
              data-testid="submit-quote-btn"
              disabled={submitting}
              className="w-full h-12 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white text-base font-semibold tracking-tight"
            >
              {submitting ? "Submitting…" : "Submit Quote"}
            </Button>
            <p className="text-[11px] text-zinc-500 text-center tracking-wide">
              By submitting, you confirm the rate is valid until{" "}
              {formatDate(inquiry.expires_at)}.
            </p>
          </form>
        )}
      </div>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="quote-confirm-dialog"
          className="sm:max-w-[520px] rounded-none border-2 border-zinc-900"
        >
          <DialogHeader>
            <div className="label-overline mb-2">Confirm Quote</div>
            <DialogTitle>Review before submitting</DialogTitle>
            <DialogDescription>
              Please confirm these details are correct. After submission, the
              inquiry owner will see your quote.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-zinc-200 divide-y divide-zinc-100 text-sm">
            {[
              ["Name / Company", form.name],
              ["Phone", form.phone_number],
              ["Rate", form.rate ? `₹${form.rate}` : ""],
              ["Vehicle", form.vehicle_type],
              ["Availability", form.availability],
              ["Remarks", form.remarks || "—"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-[130px_1fr] gap-3 p-3"
              >
                <div className="label-overline">{label}</div>
                <div className="text-zinc-900 break-words">{value}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-testid="edit-quote-btn"
              onClick={() => setConfirmOpen(false)}
              className="h-11 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
            >
              Edit
            </Button>
            <Button
              type="button"
              data-testid="confirm-submit-quote-btn"
              onClick={confirmSubmit}
              disabled={submitting}
              className="h-11 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white"
            >
              {submitting ? "Submitting..." : "Confirm & Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AppFooter />
    </div>
  );
}
